import React, { useEffect, useMemo, useCallback } from "react";
import "./Sidebar.css";
import { NavLink, useLocation } from "react-router-dom";
import { sidebarItems } from "../config/sidebar.items";
import { useDispatch, useSelector } from "react-redux";
import { setSidebarVisible } from "../../services/slices/appConfig";
import { visibleModelsSerials } from "../config/visible.shipmodel.serials";
import ecpIcon from "../../assets/ecp6.png";
import SettingsIcon from "../../pages/Settings/utils/SettingsIcon";
import { selectIsRoot } from "../../services/slices/permissionSlice";
import { navbarIcons } from "../config/navbar.icons";
import { closeChartView } from "../../services/slices/chartSlice";

function Sidebar({ setIsLoggedIn }) {
  const dispatch = useDispatch();
  const MOBILE_BREAKPOINT = 1100;

  //  Base selectors
  const envSerial = useSelector((state) => state.appConfig.envSerial);
  const rawPlcMap = useSelector((state) => state.appConfig.plcList);
  const shipSerials = useSelector((state) => state.appConfig.shipSerials);

  const reloadButtonVisible = useSelector((state) => state?.settings?.visibility?.reloadButton);

  let alarmConfigVisible = useSelector((state) => {
    const alarmsVisible = state?.settings?.visibility?.sections?.["Alarms Configuration"]?.visible;
    const plcConfig = state?.appConfig?.alarms_plc_config;
    // console.log("alarmConfigVisible conditions →", { alarmsVisible, alarms_plc_config: plcConfig });
    return Boolean(alarmsVisible && plcConfig);
  });

  const isSidebarVisible = useSelector((state) => state.appConfig.isSidebarVisible);
  const isLargeWindowSidebarVisible = useSelector(
    (state) => state.appConfig.isLargeWindowSidebarVisible,
  );
  const isRoot = useSelector(selectIsRoot);
  const canViewSensorsConfig = useSelector((state) => state.appConfig.canViewSensorsConfig);
  const canViewPlcConfig = useSelector((state) => state.appConfig.canViewPlcConfig);

  // From settings: alarms configuration target ship
  const desiredAlarmSerial = useSelector(
    (state) => state?.settings?.visibility?.sections?.["Alarms Configuration"]?.ship?.value,
  );

  // Derived values
  const alarmShipSerial = desiredAlarmSerial ?? envSerial;
  const plcList = useMemo(() => {
    const list = rawPlcMap?.[alarmShipSerial] || [];
    return Array.isArray(list) ? list : [];
  }, [rawPlcMap, alarmShipSerial]);

  const isVisibleModel = useMemo(
    () => shipSerials.some((model) => visibleModelsSerials.includes(model.serial)),
    [shipSerials],
  );

  //
  // Nraavbar classes
  const location = useLocation();
  const navbarClass = useMemo(() => {
    const sidebarClass = isSidebarVisible ? "navbar navbar-open" : "navbar navbar-closed";
    const navbarLargeWindowClass = isLargeWindowSidebarVisible
      ? "navbar-lg-open"
      : "navbar-lg-closed";
    const base = `${sidebarClass} ${navbarLargeWindowClass}`;
    return location.pathname.includes("simulator") ? `${base} sidebar-simulator` : base;
  }, [isSidebarVisible, isLargeWindowSidebarVisible, location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < MOBILE_BREAKPOINT && isSidebarVisible) {
        dispatch(setSidebarVisible(false));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarVisible, dispatch]);

  const handleNavLinkClick = useCallback(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) dispatch(setSidebarVisible(false));
  }, [dispatch]);

  const navigationItems = useMemo(() => sidebarItems.filter((item) => item.path !== "/"), []);
  const logoutItem = useMemo(() => sidebarItems.find((item) => item.path === "/"), []);

  return (
    <>
      {(isLargeWindowSidebarVisible || isSidebarVisible) && (
        <nav className={navbarClass}>
          <div className="ecp-icon-wrapper2">
            <img className="ecp-icon" src={ecpIcon} alt="ECP Icon" />
            <span className="link-text">Ship Viewer</span>
          </div>
          <ul className="navbar-nav">
            {navigationItems.map((item) => {
              if (item.path === "/simulator" && !isVisibleModel) {
                return null;
              }
              if (item.path === "/configuration" && !canViewSensorsConfig) {
                return null;
              }
              return (
                <div className="nav-item-wrapper" key={item.path}>
                  <li className="nav-item" data-tooltip={item.text}>
                    <NavLink
                      to={item.path}
                      className="nav-link"
                      onClick={() => {
                        if (item.onClick) {
                          item.onClick(setIsLoggedIn);
                        }
                        handleNavLinkClick();
                      }}
                    >
                      {item.icon}
                      <span className="link-text">{item.text}</span>
                    </NavLink>
                  </li>
                </div>
              );
            })}
            {/* ALARMS CONFIGURATION */}
            {canViewPlcConfig &&
              (isRoot || alarmConfigVisible) &&
              plcList.map((plc) => (
                <div className="nav-item-wrapper" key={plc}>
                  <li className="nav-item" data-tooltip={`AC ${plc}`}>
                    <NavLink
                      to={`/${plc}`}
                      className="nav-link plc-link"
                      onClick={handleNavLinkClick}
                    >
                      {navbarIcons.alarmPLC}
                      <span className="plc-text">{plc} </span>
                      <span className="link-text">Alarms {plc}</span>
                    </NavLink>
                  </li>
                </div>
              ))}
          </ul>

          {/* RELOAD  */}
          {reloadButtonVisible && (
            <div className="nav-item-wrapper">
              <li
                className="nav-item"
                data-tooltip="Reload Application"
                onClick={() => {
                  window.location.reload();
                }}
              >
                <div className="nav-link reload-button">
                  {navbarIcons.reload}
                  <span className="link-text">Reload</span>
                </div>
              </li>
            </div>
          )}
          {/* SETTINGS */}
          <div className="nav-item-wrapper">
            <li className="nav-item" data-tooltip="Settings">
              <NavLink
                to="/settings"
                className="nav-link settings-button"
                onClick={handleNavLinkClick}
              >
                <SettingsIcon />
                <span className="link-text">Settings</span>
              </NavLink>
            </li>
          </div>
          {/* LOGOUT  */}
          <div className="nav-item-wrapper">
            <div
              data-test-logout
              className="logout-wrapper nav-item"
              data-tooltip={logoutItem.text}
            >
              <NavLink
                to={logoutItem.path}
                className="nav-link"
                onClick={() => {
                  if (logoutItem.onClick) {
                    logoutItem.onClick(setIsLoggedIn);
                  }
                  dispatch(closeChartView());
                  handleNavLinkClick();
                }}
              >
                {logoutItem.icon}
                <span className="link-text">{logoutItem.text}</span>
              </NavLink>
            </div>
          </div>
        </nav>
      )}
    </>
  );
}

export default React.memo(Sidebar);
